/**
 * useCarePlanServiceRequests Hook
 *
 * Fetches ServiceRequests linked to a CarePlan via basedOn.
 * Used in the CarePlan detail view to display service needs for referral-enabled plans.
 * @agentic connections
 */

import { useState, useEffect, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type { ServiceRequest } from "@medplum/fhirtypes";
import { getCarePlanServiceRequests } from "@benchmark/core/services";
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Result type for useCarePlanServiceRequests.
 * @agentic connections
 */
export type UseCarePlanServiceRequestsResult = {
  serviceRequests: ServiceRequest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

/**
 * Fetch ServiceRequests for a CarePlan.
 * @agentic connections
 */
export function useCarePlanServiceRequests(
  carePlanId: string | undefined,
): UseCarePlanServiceRequestsResult {
  const medplum = useMedplum();
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServiceRequests = useCallback(async () => {
    if (!carePlanId) {
      setServiceRequests([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const results = await getCarePlanServiceRequests(medplum, carePlanId);
      setServiceRequests(results);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load service requests",
      );
      setServiceRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, carePlanId]);

  useEffect(() => {
    queueMicrotask(() => {
      void fetchServiceRequests();
    });
  }, [fetchServiceRequests]);

  // Auto-refetch when a ServiceRequest based on this CarePlan changes.
  useEventDrivenRefetch(
    [
      DomainEventNames.ServiceRequestCreated,
      DomainEventNames.ServiceRequestUpdated,
    ],
    {
      refetch: fetchServiceRequests,
      enabled: !!carePlanId,
      filter: (payload) =>
        (
          payload as { serviceRequest?: ServiceRequest }
        ).serviceRequest?.basedOn?.some(
          (ref) => ref.reference === `CarePlan/${carePlanId}`,
        ) ?? false,
    },
  );

  return {
    serviceRequests,
    isLoading,
    error,
    refetch: fetchServiceRequests,
  };
}
